<template>
  <div class="featured-experiences">
    <div class="container">
      <div class="row">
        <div class="col-12">
          <h2 class="section-title">{{ $t("experiences.featuredTitle") }}</h2>
          <p class="section-subtitle">{{ $t("experiences.featuredSubtitle") }}</p>
        </div>
      </div>
      
      <div v-if="loading" class="row">
        <div class="col-12 text-center">
          <LogoLoading />
        </div>
      </div>
      
      <div v-else-if="featuredExperiences.length > 0" class="row">
        <div class="col-md-4 col-12" v-for="(experience, index) in featuredExperiences.slice(0, 3)" :key="experience.id">
          <div class="experience-card" @click="goToExperience(experience)">
            <div class="experience-image">
              <img :src="getExperienceImage(experience)" :alt="experience.name" />
              <div class="experience-overlay">
                <h3 class="experience-title">{{ experience.name }}</h3>
                <p class="experience-description" v-html="sanitizeHtml(experience.shortDescription)"></p>
                <button class="btn btn-primary">{{ $t("experiences.learnMore") }}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div v-else class="row">
        <div class="col-12 text-center">
          <p>{{ $t("experiences.noFeatured") }}</p>
        </div>
      </div>
      
      <div class="row mt-4">
        <div class="col-12 text-center">
          <router-link :to="{ name: 'Experiences' }" class="btn btn-outline-primary">
            {{ $t("experiences.viewAll") }}
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { API_HELPER } from "@/helpers/api.js";
import LogoLoading from "@/components/LogoLoading.vue";
import InputGetList from "@/classes/InputGetList";
import { sanitizeHtml } from '@/helpers/sanitize.js';

export default {
  name: "FeaturedExperiences",
  components: {
    LogoLoading
  },
  data() {
    return {
      loading: true
    };
  },
  computed: {
    ...mapState({
      experiencesOutput: state => state.experiences.listOutput
    }),
    featuredExperiences() {
      return this.experiencesOutput && this.experiencesOutput.data ? 
        this.experiencesOutput.data.filter(exp => exp.isFeatured) : [];
    }
  },
  async mounted() {
    await this.loadFeaturedExperiences();
  },
  methods: {
    ...mapActions({
      getExperiences: 'experiences/getList'
    }),
    async loadFeaturedExperiences() {
      try {
        this.loading = true;
        
        const itemsPerPage = 6;
        const currentPage = 1;
        const sorter = 1; // asc
        const propertyName = "position";
        const searchText = '';

        const input = new InputGetList(itemsPerPage, currentPage, sorter, propertyName, searchText);
        input.isFeatured = true;

        await this.getExperiences(input);
      } catch (error) {
        console.error('Error loading featured experiences:', error);
      } finally {
        this.loading = false;
      }
    },
    getExperienceImage(experience) {
      if (experience.images && experience.images.length > 0) {
        return API_HELPER.pathJoin([process.env.VUE_APP_URL_ROOT, experience.images[0]]);
      }
      return API_HELPER.pathJoin([process.env.VUE_APP_URL_ROOT, '/default.jpg']);
    },
    goToExperience(experience) {
      this.$router.push({ 
        name: 'ExperienceDetail', 
        params: { 
          experience: experience, 
          experienceSlug: experience.slug 
        } 
      });
    },
    sanitizeHtml
  }
};
</script>

<style scoped>
.featured-experiences {
  padding: 4rem 0;
  background-color: #f8f9fa;
}

.section-title {
  font-size: 2.5rem;
  font-weight: bold;
  color: var(--primary-color);
  text-align: center;
  margin-bottom: 1rem;
}

.section-subtitle {
  font-size: 1.1rem;
  color: #666;
  text-align: center;
  margin-bottom: 3rem;
}

.experience-card {
  cursor: pointer;
  margin-bottom: 2rem;
  transition: transform 0.3s ease;
}

.experience-card:hover {
  transform: translateY(-5px);
}

.experience-image {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.experience-image img {
  width: 100%;
  height: clamp(180px, 28vh, 300px);
  object-fit: cover;
  transition: transform 0.3s ease;
}

.experience-card:hover .experience-image img {
  transform: scale(1.05);
}

.experience-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  color: white;
  padding: 2rem 1.5rem 1.5rem;
  transform: translateY(100%);
  transition: transform 0.3s ease;
}

.experience-card:hover .experience-overlay {
  transform: translateY(0);
}

.experience-title {
  font-size: 1.3rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.experience-description {
  font-size: 0.9rem;
  margin-bottom: 1rem;
  opacity: 0.9;
  max-height: 60px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  text-decoration: none;
  display: inline-block;
  font-weight: bold;
  transition: all 0.3s ease;
}

.btn-primary {
  background-color: var(--primary-color);
  color: white;
}

.btn-primary:hover {
  background-color: var(--secondary-color);
  color: white;
}

.btn-outline-primary {
  background-color: transparent;
  color: var(--primary-color);
  border: 2px solid var(--primary-color);
}

.btn-outline-primary:hover {
  background-color: var(--primary-color);
  color: white;
}

@media (max-width: 768px) {
  .section-title {
    font-size: 2rem;
  }
  
  .experience-image img {
    height: 250px;
  }
  
  .experience-overlay {
    transform: translateY(0);
    background: transparent; /* remove black bar in small screens */
    text-shadow: 0 2px 8px rgba(0,0,0,0.6);
  }
}
</style>

